// TODO: The heading <Label htmlFor> and SwitchInput's internal sr-only <Label htmlFor>
// both point at the same input, resulting in two labels for one control.
// Screen readers may announce both or behave inconsistently.
// Potential fixes:
// 1. Add aria-labelledby support to SwitchInput so the external label can be the
//    single accessible name, removing the need for the internal label entirely.
// 2. Make SwitchInput's label prop optional and omit the internal label when an
//    external label is provided (would also need to update the button's aria-label).
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Alert,
  Card,
  Icon,
  Label,
  List,
  Page,
  SwitchInput,
} from 'corso-admin-ui';
import { FormEventHandler, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AIFeatureCard } from '~/components/ai/AIFeatures';
import { useConfigSettingsUpdate } from '~/hooks/useConfigSettings';
import useIsCorsoAdmin from '~/hooks/useIsCorsoAdmin';
import { useRegistrationSettingsForm } from '~/hooks/useRegistrationSettings';
import {
  useReturnSettings,
  useReturnSettingsUpdate,
} from '~/hooks/useReturnSettings';
import { useThemeSettingsUpdate } from '~/hooks/useThemeSettings';
import { returnSettingsFormSchema } from '~/types';

function BetaBadge() {
  return (
    <span className="inline-flex cursor-default items-center gap-1 rounded-md bg-amber-500 px-1.5 py-1.5 text-[10px] leading-none font-bold tracking-wider text-white uppercase shadow-xs ring-1 ring-white/20 ring-inset">
      <Icon icon="testDriveSolid" className="size-3" />
      Beta
    </span>
  );
}

const features = [
  'Exchanges and replacements are added to the original order',
  'Store credit uses Shopify Customer Account Store Credit',
  'Financial calculations are sourced from Shopify',
  'Claim edits will cancel and recreate the Shopify return',
];

function ReturnProcess() {
  const { data } = useReturnSettings();
  const { control, handleSubmit, setValue } = useForm({
    resolver: zodResolver(returnSettingsFormSchema),
    values: data,
  });
  const isCorsoAdmin = useIsCorsoAdmin();
  const { mutateAsync: saveReturnSettings } = useReturnSettingsUpdate();
  const formRef = useRef<HTMLFormElement>(null);

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((values) => saveReturnSettings(values))(event).catch(
      console.error,
    );
  };

  const returnProcessApiId = 'return-process-api-enabled';

  if (!isCorsoAdmin) return null;

  return (
    <form ref={formRef} onSubmit={submitHandler}>
      <Card>
        <Card.Line as="header">
          <div className="flex items-center gap-3">
            <div className="flex shrink-0 items-center justify-center rounded-lg bg-amber-500 p-2.5">
              <Icon icon="testDriveSolid" className="size-5 text-white" />
            </div>
            <Label
              htmlFor={returnProcessApiId}
              className="cursor-pointer text-sm font-bold text-neutral-900 opacity-75"
            >
              Shopify Return Processing
            </Label>
            <Controller
              control={control}
              disabled={!isCorsoAdmin}
              name="returns.isReturnProcessApiEnabled"
              defaultValue={false}
              render={({ field: { value }, fieldState }) => (
                <SwitchInput
                  id={returnProcessApiId}
                  label="Enable"
                  labelVisuallyHidden
                  checked={value}
                  onChange={() => {
                    setValue('returns.isReturnProcessApiEnabled', !value, {
                      shouldDirty: true,
                    });
                    formRef.current?.requestSubmit();
                  }}
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
          <BetaBadge />
        </Card.Line>

        <Card.Description>
          When enabled, exchanges, replacements, refunds, and store credits are
          processed as a single operation on the original order using
          Shopify&apos;s native Return APIs, no separate orders are created.{' '}
          <Action
            variant="link"
            to="https://help.corso.com/returns-exchange/migrate-to-return-process/"
            target="_blank"
            rel="noreferrer"
          >
            Learn more
          </Action>
          .
        </Card.Description>

        <List items={features} columns={2}>
          {(feature) => <List.Item key={feature}>{feature}</List.Item>}
        </List>
      </Card>
    </form>
  );
}

const smartRegistrationFeatures = [
  'Receipt & invoice parsing',
  'Auto product matching',
  'SKU & variant detection',
  'Faster customer experience',
];

function SmartRegistration() {
  const isCorsoAdmin = useIsCorsoAdmin();
  const { control, handleSubmit, setValue } = useRegistrationSettingsForm();
  const { mutateAsync: saveRegistrationSettings } = useConfigSettingsUpdate();
  const { mutateAsync: saveThemeSettings } = useThemeSettingsUpdate();
  const formRef = useRef<HTMLFormElement>(null);

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(({ theme: themeChanges, ...registrations }) =>
      Promise.all([
        saveRegistrationSettings({ registrations }),
        saveThemeSettings(themeChanges),
      ]),
    )(event).catch(console.error);
  };

  return (
    <form ref={formRef} onSubmit={submitHandler}>
      <AIFeatureCard
        title="Smart Registrations"
        description="Let AI do the heavy lifting. Customers upload a proof of purchase and our AI automatically extracts product details, matches them to your catalog, and populates the registration form which reduces mistakes and speeds up the registration process."
        features={smartRegistrationFeatures}
        action={
          <Controller
            control={control}
            name="isAutomatedRegistrationEnabled"
            defaultValue={false}
            render={({ field: { value }, fieldState }) => (
              <SwitchInput
                id="allow-automated-registration"
                label="Enable"
                labelVisuallyHidden
                checked={!!value}
                onChange={() => {
                  setValue('isAutomatedRegistrationEnabled', !value, {
                    shouldDirty: true,
                  });
                  formRef.current?.requestSubmit();
                }}
                error={fieldState.error?.message}
                disabled={!isCorsoAdmin}
              />
            )}
          />
        }
      />
    </form>
  );
}

const exchangeRecommendationFeatures = [
  'Personalized variant recommendations',
  'Reason-aware product matching',
  'Learns from your historical exchanges',
  'Faster customer decision-making',
];

function ExchangeRecommendation() {
  const { data } = useReturnSettings();
  const { control, handleSubmit, setValue } = useForm({
    resolver: zodResolver(returnSettingsFormSchema),
    values: data,
  });
  const isCorsoAdmin = useIsCorsoAdmin();
  const { mutateAsync: saveReturnSettings } = useReturnSettingsUpdate();
  const formRef = useRef<HTMLFormElement>(null);

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((values) => saveReturnSettings(values))(event).catch(
      console.error,
    );
  };

  return (
    <form ref={formRef} onSubmit={submitHandler}>
      <AIFeatureCard
        title="Exchange Recommendations"
        description="Help customers find their next favorite. Our AI suggests the best exchange variant based on the return reason, your historical exchange data, and product fit so customers spend less time searching and more time shopping."
        features={exchangeRecommendationFeatures}
        action={
          <Controller
            control={control}
            name="variantExchanges.isExchangeRecommendationEnabled"
            defaultValue={false}
            render={({ field: { value }, fieldState }) => (
              <SwitchInput
                id="exchange-recommendation-enabled"
                label="Enable"
                labelVisuallyHidden
                checked={!!value}
                onChange={() => {
                  setValue(
                    'variantExchanges.isExchangeRecommendationEnabled',
                    !value,
                    { shouldDirty: true },
                  );
                  formRef.current?.requestSubmit();
                }}
                error={fieldState.error?.message}
                disabled={!isCorsoAdmin}
              />
            )}
          />
        }
      />
    </form>
  );
}

function TestDriveIntro() {
  return (
    <Alert elevated variant="tip" heading="Try Upcoming Features">
      A look at what we&apos;re working on next. Features in various stages of
      development that we&apos;re excited enough to let you take for a spin.
      Reach out to{' '}
      <Action variant="link" to="mailto:priority@corso.com">
        priority@corso.com
      </Action>{' '}
      to get started.
    </Alert>
  );
}

export default function TestDriveSettingsPage() {
  return (
    <Page title="Test Drive">
      <div className="flex flex-col gap-4">
        <TestDriveIntro />
        <SmartRegistration />
        <ExchangeRecommendation />

        <ReturnProcess />
      </div>
    </Page>
  );
}
